import { useCallback, useEffect, useRef, useState } from 'react';
import userSettingsService from '../services/userSettings.service';

const SETTING_KEY = 'favorites';

export interface FavoriteItem {
  name: string;
  href: string;
}

interface FavoritesValue {
  items: FavoriteItem[];
}

function sortAlpha(items: FavoriteItem[]): FavoriteItem[] {
  return [...items].sort((a, b) =>
    a.name.toLowerCase().localeCompare(b.name.toLowerCase()),
  );
}

export function useFavorites() {
  const [items, setItems] = useState<FavoriteItem[]>([]);
  const itemsRef = useRef<FavoriteItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const setting = await userSettingsService.getUserSetting(SETTING_KEY);
        if (cancelled) return;
        const value = (setting?.setting_value as FavoritesValue | undefined) ?? { items: [] };
        itemsRef.current = sortAlpha(value.items ?? []);
        setItems(itemsRef.current);
      } catch {
        if (!cancelled) setItems([]);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const isFavorited = useCallback(
    (href: string) => items.some((f) => f.href === href),
    [items],
  );

  // Last-write-wins: concurrent rapid toggles race at the PUT layer. Acceptable for v1 single-user starring.
  const toggleFavorite = useCallback(async (name: string, href: string) => {
    const previous = itemsRef.current;
    const next = previous.some((f) => f.href === href)
      ? previous.filter((f) => f.href !== href)
      : [...previous, { name, href }];
    const sorted = sortAlpha(next);
    itemsRef.current = sorted;
    setItems(sorted);
    try {
      await userSettingsService.saveUserSetting(SETTING_KEY, { items: sorted });
    } catch (err) {
      if (itemsRef.current === sorted) {
        itemsRef.current = previous;
        setItems(previous);
      }
      throw err;
    }
  }, []);

  return { favorites: items, isFavorited, toggleFavorite, isLoading };
}
